import { useFileRecordDownload } from "@/hooks/useFileRecordDownload";
import { attachFileRecordDownloadVariants } from "@/object-actions/attachFileRecordDownloadVariants";
import {
  type FileRecordActionHandlerDeps,
  useFileRecordObjectActionHandlers,
} from "@/object-actions/handlers/fileRecord";
import {
  createRemoteFileRecordExportRunner,
  type RemoteFileRecordDownloadFn,
} from "@/object-actions/handlers/fileRecordDownload";
import { useObjectActions } from "@/object-actions/renderers/useObjectActions";
import type {
  ActionScope,
  ActionSurface,
  ObjectActionContext,
  ObjectActionHandlers,
  ObjectKind,
  ResolvedObjectAction,
} from "@/object-actions/types";
import {
  fileRecordEverPublished,
  resolveFileRecordShowPublish,
} from "@/panes/file-record-detail/publish";
import type { FileRecord } from "@cohesive/api-client";
import { RESOURCE_TAG_FAVORITE } from "@cohesive/api-client";
import { useMemo } from "react";
import type { FileRecordDownloadExpandParams } from "./attachFileRecordDownloadVariants";

export function buildFileRecordKind(
  record: FileRecord,
): Extract<ObjectKind, { type: "file-record" }> {
  return {
    type: "file-record",
    class: record.class,
    isFolder: record.path?.endsWith("/") ?? false,
  };
}

export interface UseResolvedFileRecordActionsParams {
  record: FileRecord | null;
  surface: ActionSurface;
  enabled?: boolean;
  scope?: ActionScope;
  handlerDeps?: FileRecordActionHandlerDeps;
  extraHandlers?: ObjectActionHandlers;
  state?: Partial<ObjectActionContext["state"]>;
  /** When set, uses live editor/export context instead of remote fetch. */
  downloadExpand?: Omit<FileRecordDownloadExpandParams, "record"> | null;
}

export function useResolvedFileRecordActions({
  record,
  surface,
  enabled = true,
  scope = "canonical",
  handlerDeps = {},
  extraHandlers = {},
  state = {},
  downloadExpand,
}: UseResolvedFileRecordActionsParams): {
  actions: ResolvedObjectAction[];
  kind: Extract<ObjectKind, { type: "file-record" }> | null;
} {
  const kind = useMemo(
    () => (record ? buildFileRecordKind(record) : null),
    [record],
  );
  const isFavorite = !!record?.tags?.includes(RESOURCE_TAG_FAVORITE);

  const baseHandlers = useFileRecordObjectActionHandlers(
    record ?? ({} as FileRecord),
    handlerDeps,
  );
  const handlers = useMemo(
    () => ({ ...baseHandlers, ...extraHandlers }),
    [baseHandlers, extraHandlers],
  );

  const { actions: resolvedActions } = useObjectActions({
    kind: kind ?? { type: "file-record", class: "workspace", isFolder: false },
    surface,
    scope,
    enabled: enabled && !!record && !!kind,
    state: {
      isFavorite,
      isPublished: !!record && fileRecordEverPublished(record),
      canShare: record?.can_edit !== false,
      canEdit: record?.can_edit !== false,
      canDelete: !!handlerDeps.onDelete,
      showPublish:
        state.showPublish ??
        (record
          ? resolveFileRecordShowPublish({
              record,
              canEdit: state.canEdit ?? record.can_edit !== false,
              scope,
            })
          : false),
      ...state,
    },
    handlers,
  });

  const { download } = useFileRecordDownload();
  const remoteExport = useMemo(
    () =>
      record ? createRemoteFileRecordExportRunner(record, download) : null,
    [download, record],
  );

  const actions = useMemo(() => {
    if (!record) {
      return [];
    }

    const expandParams: FileRecordDownloadExpandParams | null =
      downloadExpand === null
        ? null
        : {
            record,
            runExport:
              downloadExpand?.runExport ??
              remoteExport?.runExport ??
              (async () => {}),
            isVariantDisabled:
              downloadExpand?.isVariantDisabled ??
              remoteExport?.isVariantDisabled,
          };

    return attachFileRecordDownloadVariants(resolvedActions, expandParams);
  }, [downloadExpand, record, remoteExport, resolvedActions]);

  return { actions, kind };
}

export type { RemoteFileRecordDownloadFn };
